import { getAttachments } from "api/services/request";
import { downloadAllQuickRequestAttachments } from "api/services/quickRequest";
import { getAllAttachments } from "api/services/shipment";
import showErrorToast from "helpers/errorToast";

export type ShipmentAttachment = {
  id: number | string;
  bucket_name: string;
  url: string;
  name: string;
  size: number | null;
};

type BucketAttachmentItem = {
  id?: number | string;
  bucket_name?: string;
  url?: string;
  name?: string;
  size?: number | string | null;
  file?: {
    id?: number | string;
    url?: string;
    name?: string;
    size?: number | string | null;
  };
  attachment_bucket?: { bucket_name?: string };
};

function parseFileSize(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return null;
}

export function formatBucketData(data: BucketAttachmentItem[] = []): ShipmentAttachment[] {
  return data.map((item) => ({
    id: item?.file?.id || item?.id || "",
    bucket_name: item?.attachment_bucket?.bucket_name || item?.bucket_name || "",
    url: item?.file?.url || item?.url || "",
    name: item?.file?.name || item?.name || "",
    size: parseFileSize(item?.file?.size ?? item?.size),
  }));
}

export async function fetchShipmentAttachments(shipmentId: string): Promise<ShipmentAttachment[]> {
  const response = await getAttachments(shipmentId);
  return [
    ...formatBucketData(response.data.bucket_attachments as BucketAttachmentItem[]),
    ...formatBucketData(response.data.other_attachments as BucketAttachmentItem[]),
  ];
}

export async function downloadAllShipmentAttachments(
  shipmentId: string,
  isQuickRequest = false,
): Promise<void> {
  const response = isQuickRequest
    ? await downloadAllQuickRequestAttachments(shipmentId)
    : await getAllAttachments(shipmentId);
  const blob = new Blob([response.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `attachments-${shipmentId}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadAllShipmentAttachmentsWithErrorHandling(
  shipmentId: string,
  onFinally?: () => void,
  isQuickRequest = false,
): void {
  downloadAllShipmentAttachments(shipmentId, isQuickRequest)
    .catch((error) => showErrorToast(error))
    .finally(() => onFinally?.());
}
